import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../../services/api';
import './SettingsPage.css';

function SettingsPage({ onDataCleared }) {
  const navigate = useNavigate();
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showExportSuccess, setShowExportSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [clearPassword, setClearPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [notification, setNotification] = useState(null);
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleClearData = () => {
    setShowClearConfirm(true);
    setClearPassword('');
    setConfirmText('');
    setError('');
  };

  const handleConfirmClear = async () => {
    if (confirmText !== 'DELETE') {
      setError('Please type DELETE to confirm');
      return;
    }

    if (!clearPassword) {
      setError('Password is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await api.entries.clearAllData(clearPassword);
      setShowClearConfirm(false);
      
      // Notify parent component that data was cleared
      if (onDataCleared) {
        onDataCleared();
      }
      
      // Show success message briefly then navigate
      const entryText = result.deletedCount === 1 ? 'entry' : 'entries';
      showNotification(`${result.deletedCount} ${entryText} deleted successfully`, 'success');
      setTimeout(() => navigate('/'), 2000);
    } catch (err) {
      setError(err.message || 'Failed to clear data');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setLoading(true);
    
    try {
      await api.entries.requestEmailExport();
      
      // Show success popup
      setShowExportSuccess(true);
    } catch (err) {
      console.error('Export failed:', err);
      showNotification(err.message || 'Failed to request export', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="settings-page">
      <div className="settings-page__container">
        <div className="settings-page__header">
          <button 
            className="settings-page__back"
            onClick={() => navigate('/')}
            aria-label="Back to home"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
          </button>
          <h2 className="settings-page__title">Settings</h2>
          <div style={{ width: '44px' }}></div> {/* Spacer for alignment */}
        </div>

        <div className="settings-page__sections">
          <section className="settings-page__section">
            <h3 className="settings-page__section-title">Data</h3>
            
            <button 
              className="settings-page__action-btn"
              onClick={handleExport}
              disabled={loading}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/>
              </svg>
              Export Entries
            </button>

            <button 
              className="settings-page__action-btn settings-page__action-btn--danger"
              onClick={handleClearData}
              disabled={loading}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              </svg>
              Clear All Data
            </button>
          </section>

          <section className="settings-page__section">
            <h3 className="settings-page__section-title">About</h3>
            <div className="settings-page__about">
              <p className="settings-page__about-text">
                <strong>Remori</strong> — A return to memory
              </p>
              <p className="settings-page__about-text">
                Version 1.0.1
              </p>
              <p className="settings-page__about-text">
                A private space to write freely and understand your emotions over time.
              </p>
              <div className="settings-page__about-links">
                <Link to="/about" className="settings-page__about-link">About</Link>
                <span className="settings-page__about-sep" aria-hidden="true">·</span>
                <Link to="/privacy" className="settings-page__about-link">Privacy Policy</Link>
                <span className="settings-page__about-sep" aria-hidden="true">·</span>
                <Link to="/terms" className="settings-page__about-link">Terms of Service</Link>
                <span className="settings-page__about-sep" aria-hidden="true">·</span>
                <a
                  href="https://devhopeslabs.com/contact"
                  className="settings-page__about-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Raise a Complaint
                </a>
              </div>
            </div>
          </section>
        </div>

        {/* Clear Data Confirmation */}
        {showClearConfirm && (
          <>
            <div className="settings-page__confirm-overlay" onClick={() => setShowClearConfirm(false)} />
            <div className="settings-page__confirm-popup">
              <div className="settings-page__confirm-icon settings-page__confirm-icon--danger">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              </div>
              <h3 className="settings-page__confirm-title">Clear All Data?</h3>
              <p className="settings-page__confirm-message">
                This will permanently delete all your journal entries. This action cannot be undone.
              </p>
              
              {error && (
                <div className="settings-page__error">
                  {error}
                </div>
              )}
              
              <div className="settings-page__confirm-fields">
                <div className="settings-page__field">
                  <label className="settings-page__field-label">
                    Enter your password
                  </label>
                  <input
                    type="password"
                    className="settings-page__field-input"
                    placeholder="Your password"
                    value={clearPassword}
                    onChange={(e) => setClearPassword(e.target.value)}
                    disabled={loading}
                  />
                </div>
                
                <div className="settings-page__field">
                  <label className="settings-page__field-label">
                    Type <strong>DELETE</strong> to confirm
                  </label>
                  <input
                    type="text"
                    className="settings-page__field-input"
                    placeholder="DELETE"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    disabled={loading}
                  />
                </div>
              </div>
              
              <div className="settings-page__confirm-actions">
                <button 
                  className="settings-page__confirm-btn settings-page__confirm-btn--cancel"
                  onClick={() => setShowClearConfirm(false)}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button 
                  className="settings-page__confirm-btn settings-page__confirm-btn--danger"
                  onClick={handleConfirmClear}
                  disabled={loading || confirmText !== 'DELETE' || !clearPassword}
                >
                  {loading ? 'Deleting...' : 'Delete Everything'}
                </button>
              </div>
            </div>
          </>
        )}

      </div>

      {/* Export Success Popup */}
      {showExportSuccess && (
        <>
          <div className="settings-page__confirm-overlay" onClick={() => setShowExportSuccess(false)} />
          <div className="settings-page__confirm-popup">
            <div className="settings-page__confirm-icon settings-page__confirm-icon--success">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <h3 className="settings-page__confirm-title">Export Requested</h3>
            <p className="settings-page__confirm-message">
              Your data will be sent to your email id soon. Please check your inbox.
            </p>
            <div className="settings-page__confirm-actions">
              <button 
                className="settings-page__confirm-btn settings-page__confirm-btn--cancel"
                onClick={() => setShowExportSuccess(false)}
              >
                OK
              </button>
            </div>
          </div>
        </>
      )}

      {/* Notification Overlay */}
      {notification && (
        <div className="settings-page__notification-overlay">
          <div className={`settings-page__notification settings-page__notification--${notification.type}`}>
            <div className="settings-page__notification-icon">
              {notification.type === 'success' ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              )}
            </div>
            <p className="settings-page__notification-message">{notification.message}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default SettingsPage;
